import { useFeatureFlag } from '@src/hooks';
import type { useDisclosureWithContext } from '@talos/kyoko';
import {
  Button,
  ButtonGroup,
  ButtonVariants,
  Flex,
  formattedDate,
  InlineFormattedNumber,
  Input,
  PreviewView,
  Text,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import type { CustomerTransactionForm } from './types';

const EXPECTED_CONFIRM_TEXT = 'confirm';

interface BalanceTransactionPreviewProps {
  previewDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ form: CustomerTransactionForm; isForceWithdraw: boolean }>
  >;
  onConfirmTransaction: () => Promise<void>;
}

export function BalanceTransactionPreview({ previewDisclosure, onConfirmTransaction }: BalanceTransactionPreviewProps) {
  const { requireAdditionalConfirmOnBalanceTransactionPreview } = useFeatureFlag();
  const [confirmInput, setConfirmInput] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onHandleCancel = useCallback(() => {
    previewDisclosure.close();
    setConfirmInput('');
  }, [previewDisclosure]);

  const onHandleConfirm = useCallback(() => {
    setIsSubmitting(true);
    onConfirmTransaction()
      .then(() => {
        previewDisclosure.close();
      })
      .finally(() => {
        setConfirmInput('');
        setIsSubmitting(false);
      });
  }, [previewDisclosure, onConfirmTransaction]);

  if (previewDisclosure.context === undefined) {
    return null;
  }

  const { form, isForceWithdraw } = previewDisclosure.context;
  const disabledDueToConfirmInput =
    requireAdditionalConfirmOnBalanceTransactionPreview && confirmInput.toLowerCase() !== EXPECTED_CONFIRM_TEXT;

  return (
    <PreviewView
      disclosure={previewDisclosure}
      header="Confirm Transaction"
      footer={
        <ButtonGroup gap="spacing12">
          <Button
            data-testid="preview-balance-cancel"
            variant={ButtonVariants.Default}
            onClick={onHandleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            data-testid="balance-transaction-preview-confirm-button"
            variant={ButtonVariants.Primary}
            onClick={onHandleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting || disabledDueToConfirmInput}
          >
            Confirm
          </Button>
        </ButtonGroup>
      }
    >
      <Flex flexDirection="column">
        <Flex p="spacing12" flexDirection="column" gap="spacing8">
          {form.Counterparty && (
            <ItemRow>
              <Text>Customer</Text>
              <Text>{form.Counterparty}</Text>
            </ItemRow>
          )}
          {form.MarketAccount && (
            <ItemRow>
              <Text>Customer Account</Text>
              <Text>{form.MarketAccount}</Text>
            </ItemRow>
          )}
          {form.FromMarketAccount && (
            <ItemRow>
              <Text>From Market Account</Text>
              <Text>{form.FromMarketAccount}</Text>
            </ItemRow>
          )}
          {form.ToMarketAccount && (
            <ItemRow>
              <Text>To Market Account</Text>
              <Text>{form.ToMarketAccount}</Text>
            </ItemRow>
          )}
          {form.Amount && (
            <ItemRow>
              <Text>Amount</Text>
              <InlineFormattedNumber number={form.Amount} currency={form.Currency} trimTrailingZeroes />
            </ItemRow>
          )}
          {form.Type && (
            <ItemRow>
              <Text>Type</Text>
              <Text>{form.Type}</Text>
            </ItemRow>
          )}
          {form.Comments && (
            <ItemRow>
              <Text>Comments</Text>
              <Text>{form.Comments}</Text>
            </ItemRow>
          )}
          {form.ExternalComments && (
            <ItemRow>
              <Text>External Comments</Text>
              <Text>{form.ExternalComments}</Text>
            </ItemRow>
          )}
          {form.ExternalID && (
            <ItemRow>
              <Text>External ID</Text>
              <Text>{form.ExternalID}</Text>
            </ItemRow>
          )}
          {form.TxHash && (
            <ItemRow>
              <Text>TX Hash</Text>
              <Text>{form.TxHash}</Text>
            </ItemRow>
          )}
          {form.TransactTime && (
            <ItemRow>
              <Text>Transaction Time</Text>
              <Text>{formattedDate(form.TransactTime)}</Text>
            </ItemRow>
          )}
          {isForceWithdraw && (
            <ItemRow>
              <Text>Force Withdraw</Text>
              <Text>Yes</Text>
            </ItemRow>
          )}

          {requireAdditionalConfirmOnBalanceTransactionPreview && (
            <ItemRow mt="spacing4">
              {`Type "${EXPECTED_CONFIRM_TEXT}" to continue with the transaction`}
              <Input
                width="12ch"
                value={confirmInput}
                onChange={e => setConfirmInput(e.target.value)}
                data-testid="balance-transaction-preview-confirm-input"
              />
            </ItemRow>
          )}
        </Flex>
      </Flex>
    </PreviewView>
  );
}

const ItemRow = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing8}px;
  align-items: center;

  > *:last-child {
    color: ${({ theme }) => theme.colorTextImportant};
  }
`;
